export default {
  // Gasless meta-tx relay — POST { req, signature } (EIP-712 ForwardRequest), backend pays gas.
  // See services/relay.service.ts; request/response shape per FRONTEND_INTEGRATION_GUIDE §4.
  relay: "/relay",
  // Everything needed to build and sign a ForwardRequest — nonce plus the EIP-712 domain/types
  // generated from the same config the relay validates against, so the domain can't drift from
  // the deployed forwarder.
  relayNonce: (address: string) => `/relay/nonce/${address}`,
  // Which allowlisted functions this wallet may relay, given its org-graph role. Use it to
  // decide what to render instead of probing with requests and collecting 403s.
  relayCapabilities: (address: string) => `/relay/capabilities/${address}`,
  // V9.1 post-auction settlement state machine (FRONTEND_INTEGRATION_GUIDE §8).
  settlement: {
    // Full record plus derived nextAction / nextActor / deadlineAt — the deadlines come from
    // server-side window config, so never recompute them client-side.
    byToken: (tokenId: string | number) => `/settlement/${tokenId}`,
    // Also reads EcoBank.getSettlement and returns onChain.drift — true when the backend's
    // mirror disagrees with the chain. First thing to check on an apparently stuck settlement.
    byTokenOnChain: (tokenId: string | number) => `/settlement/${tokenId}?onChain=true`,
    history: (tokenId: string | number) => `/settlement/${tokenId}/history`,
    mine: (address: string) => `/settlement/mine?address=${address}`,
    list: "/settlement",
    // The GTA's covenant-term adjudication queue. Not `list?state=…` — a term challenge has no
    // settlement state (the settlement stays FINALISED throughout), so it needs its own route.
    termChallenges: "/settlement/term-challenges",
  },
  // V10.1.2 panel verdicts (EcoChallenge.recordOutcome). Each seat POSTs its EIP-712 Verdict
  // signature (JWT); the server recovers the signer, refuses anyone not seated, and once one
  // verdict reaches the threshold queues recordOutcome on its challenge worker from the server
  // wallet. GET is public and returns { seated, threshold, panel, judgeNonce, verdicts[] }.
  challenges: {
    verdictSignatures: (challengeId: string | number | bigint) => `/challenges/${challengeId}/verdict-signatures`,
  },
  // Pre-mint counterpart of `challenges` above (Governance.judgePreMint). Grouped by GROUND rather
  // than a single panel — SCORE draws a 3-seat panel deciding by 2, DOCUMENTS a single checker —
  // so GET returns { requestId, nonce, byGround: [{ ground, threshold, panel, verdicts[] }] } and
  // POST takes { ground, upheld, signature }.
  preMintChallenges: {
    verdictSignatures: (requestId: string | number | bigint) => `/pre-mint-challenges/${requestId}/verdict-signatures`,
  },
  // Reconstructed org graph — role, org, country, admitting holder, and why a wallet is inactive
  // (`suspended`/`frozen` are reversible, `dismissed`/`removed` are not).
  orgGraph: {
    member: (address: string) => `/org-graph/members/${address}`,
    members: "/org-graph/members",
    org: (orgId: string | number) => `/org-graph/orgs/${orgId}`,
    // The legacy first holder. V10.1: GTAs are the Governance registry (Governance.isGTA) and
    // judging is permissionless — the panel's signatures are the authority, not a relay role.
    globalAdmin: "/org-graph/global-admin",
    // The active GTA registry seats: { count, panelSeatsRequired, panelReady, gtas: [{ walletAddress, … }] }.
    gtas: "/org-graph/gtas",
  },
  admin: {
    organizations: "/admin/organizations",
    organization: (id: string | number) => `/admin/getOrdByOrgId/${id}`,
    verifyOrganization: (id: string | number) => `/admin/organizations/${id}`,
    checkWallet: (address: string) => `/admin/checkWallet/${address}`,
    inviteTrustAdmin: "/admin/inviteTrustAdmin",
    becomeTrustAdminOrg: "/admin/becomeTrustAdminOrg",
    trustAdminsKycVerified: "/admin/trustAdmins/kyc-verified",
    verifyUser: "/admin/makeUserTrust",
    reassignToken: "/admin/reassignLand",
    pendingReassign: (tokenID: string) => `/admin/pendingReassignees/${tokenID}`,
    verifiedBy: (userAddress: string) => `/admin/verified-by?userAddress=${userAddress}`,
  },
  user: {
    root: "/user",
    getRegions: (country: string) => `/user/states-by-country?country=${country}`,
    getAvailableCountries: "/user/available-countries",
    // Every country (public, server-cached 24h). Unlike /available-countries this is not
    // filtered down to countries that have an ecoFutures admin.
    getAllCountries: "/user/all-countries",
    wallet: "/user/wallet",
    checkWallet: (address: string) => `/consolidated/public-wallet/${address}`,
    authString: "/user/auth-string",
    signIn: "/user/sign-in",
    getCoordinates: "user/address-from-coordinates",
    acceptTrustee: "/user/accept-trustee",
    declineTrustee: "/user/decline-trustee",
    declineTrust: "/trust/rejectTrustInvitation",
    getToken: (tokenId: string) => `/user/eco-tokens/${tokenId}`,
    profile: {
      picture: "/user/profile-picture",
      proofOfId: "/user/proof-of-id",
      tokens: {
        root: "/user/eco-tokens",

        requestVerification: "/eco-token/request-verification",
      },
    },
    verification: {
      code: {
        send: "/user/get-referral-code",
        apply: "/user/verify-referral-code",
        adminSend: "/admin/inviteAdmin",
      },
      kyc: {
        status: "/kyc/status",
        createApplicant: "/kyc/create-applicant",
        submit: "/kyc/submit",
      },
      email: {
        request: "/user/email",
        verify: "/user/email/verify",
        requestVerification: "/user/email/verification-code",
      },
    },
  },
  trust: {
    root: "/trust",
    getFlags: (address: string) => `/trust-dismissal/getFlagsCount/${address}`,
    myOrganization: "/trust/myOrganization",
    becomeTrust: "/trust/becomeTrust",
    organization: "/trust/updateOrganization",
    organizationUsers: "/trust/organization/users",
    rejectTrustee: "/trust/rejectTrustee",
    organizationInvite: "trust/inviteTrustee",
    tokens: "/trust/tokens",
    availableTokens: "/trust/tokens/available",
    getVerifications: "/trust/tokens/verification",
    // Deeds anchored and awaiting an independent attestor that THIS wallet may actually
    // attest. The backend applies the eligibility rules the client cannot compute — same
    // country, and not verified by anyone in the caller's on-chain org (EcoBank's
    // NotIndependentAttestor). Every row it omits is a call that would have reverted.
    attestation: "/trust/tokens/attestation",
    // The pre-mint watchdog queue, one lifecycle stage earlier than `attestation`: verifications
    // submitted on chain that THIS wallet may dispute with Core.challenge(requestId, evidence).
    // The backend applies the rules the client cannot compute — APPROVED and unminted, not
    // already challenged, and scored by a verifier outside the caller's ON-CHAIN org (which is a
    // different number space from the KYC organisation).
    review: "/trust/tokens/review",
    // Every verification with a standing pre-mint challenge — the GTA's adjudication queue for
    // Governance.judgePreMint (V10.1). Admin-only. Deliberately not `/settlement?state=challenged`: that one
    // is the deed challenge, which is keyed on a tokenId and has a settlement state; this blocks
    // a mint that has not happened, so it has neither.
    preMintChallenges: "/trust/challenges",
    claim: "/trust/tokens/claim",
    complaints: {
      root: "/trust/complaints",
      byId: (id: string | number) => `/trust/complaints/${id}`,
      respond: (id: string | number) => `/trust/complaints/${id}/respond`,
    },
    easement: "/trust/tokens/easement",
    additionalContent: "/trust/tokens/additional-content",
    easementContract: "/trust/tokens/easement-contract",
    prepareApproval: "/trust/tokens/:id/prepare-approval",
    approve: "/trust/tokens/:id/approve",
    prepareReVerificationApproval: "/trust/tokens/:id/prepareReverificationApproval",
    approveReVerification: "/trust/tokens/:id/approveReVerification",
    reject: "/trust/tokens/:id/reject",
    wallet: "/trust/wallet",
    unverified: "/trust/unverifiedTokens",
    assessments: {
      get: "/assessments/indicators",
      create: "/assessments",
    },
    zoneScores: {
      get: (tokenId: string | number, indicatorId: string | number) =>
        `/assessments/zone-scores?tokenId=${tokenId}&indicatorId=${indicatorId}`,
      create: "/assessments/zone-scores",
    },
    checkUser: (address: string) => `/trust/checkWallet/${address}`,
    claimUnassigned: `/trust/tokens/claimUnassigned`,
    acceptReassign: `/trust/acceptReassign`,
    rejectReassign: `/trust/rejectReassign`,
  },
  ecoBank: {
    allStats: "/eco-bank/stats",
    stats: (count: number) => `/eco-bank/stats?count=${count}`,
    search: "/eco-bank/search",
    indicators: {
      root: "/eco-bank/indicators",
    },
    transactions: {
      root: "/eco-bank/transactions",
      byAddress: (address: string) => `/eco-bank/transactions?address=${address}`,
      byName: (name: string) => `/eco-bank/transactions?address=${name}`,
      byNft: (nftId: string | number) => `/eco-bank/transactions?nftId=${nftId}`,
    },
    lands: {
      root: "/eco-bank/lands",
      byAddress: (address: string) => `/eco-bank/lands?address=${address}`,
      search: (search: string) => `/search/ecoToken?query=${encodeURIComponent(search)}`,
    },
  },
  token: {
    auction: {
      getInfo: (nftId: number) => `/auction?nftId=${nftId}`,
    },
    getFullData: (tokenId: string) => `/eco-token/get-full-data/${tokenId}`,
    create: "/eco-token/create",
    postDraft: "/eco-token/draft",
    draftId: (tokenId: string) => `/eco-token/draft/${tokenId}`,
    list: "/eco-token",
    getUploadLink: "/eco-token/getContentUploadLink",
    offers: (tokenId: string) => `/eco-token/get-offers/${tokenId}`,
    assets: "/eco-token/assets/own",
    deleteContent: (contentId: string) => `/eco-token/content/${contentId}`,
    minted: (userAddress: string) => `/eco-token?mintedBy=${userAddress}`,
    soldBy: (userAddress: string) => `/eco-token?soldBy=${userAddress}`,
    details: (tokenId: string) => `/eco-token/${tokenId}`,
    trustee: (trustee: string) => `/eco-token?trustee=${trustee}`,
    update: (tokenId: string) => `/eco-token/${tokenId}`,
  },
  notifications: {
    // Paginated feed: ?page=1&limit=20&unreadOnly=false (dismissed excluded server-side)
    list: "/notifications",
    // Lightweight bell-badge count: { unread: number }
    count: "/notifications/count",
    // Real-time SSE stream — server pushes JSON-serialised Notification objects as data: frames
    stream: "/notifications/stream",
    // PATCH — mark every notification as read
    readAll: "/notifications/read-all",
    // PATCH — mark a single notification as read
    read: (id: string) => `/notifications/${id}/read`,
    // DELETE — soft-dismiss every notification
    dismissAll: "/notifications",
    // DELETE — soft-dismiss a single notification
    dismiss: (id: string) => `/notifications/${id}`,
  },
  tree: {
    claiming: "/tree/claiming",
  },
  map: {
    root: "/map",
    getById: (tokenId: string | number) => `/map/:${tokenId}`,
  },
  ipfsDocument: {
    templates: (type: string) => `/ipfs-document/templates/${type}`,
    create: '/ipfs-document',
    get: (id: number) => `/ipfs-document/${id}`,
    templateVars: (id: number) => `/ipfs-document/${id}/template-vars`,
    signatories: (id: number) => `/ipfs-document/${id}/signatories`,
    sign: (id: number) => `/ipfs-document/${id}/sign`,
    finalize: (id: number) => `/ipfs-document/${id}/finalize`,
    preview: (id: number) => `/ipfs-document/${id}/preview`,
    download: (id: number) => `/ipfs-document/${id}/download`,
    downloadForSigning: (id: number) => `/ipfs-document/${id}/download-for-signing`,
    wetInkSignature: (id: number) => `/ipfs-document/${id}/wet-ink-signature`,
  },
};
